import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import constants from "../../Constants";
import "../integrations/integrations.css";
import "./support.css";

const baseURL = constants.baseURL;

const DEFAULT_CONTACTS = {
  whatsapp: [
    {
      number: "+27663784460",
      label: "Business support",
      description: "Fastest for urgent trip or billing queries",
      available: "Monday - Friday, 8am - 6pm",
    },
  ],
  call: [
    {
      number: "+27872502919",
      label: "Voice line",
      description: "Trip help and account queries",
      available: "Monday - Friday, 8am - 6pm",
    },
  ],
  email: "support@qiktruck.co.za",
  supportHours: "Monday - Friday, 8am - 6pm",
  social: {},
};

const HELP_TOPICS = [
  {
    href: "/dashboard",
    icon: "mdi-view-dashboard",
    tone: "teal",
    title: "Track deliveries",
    desc: "Live order status and driver tracking",
  },
  {
    href: "/billing",
    icon: "mdi-receipt",
    tone: "orange",
    title: "Billing & EFT",
    desc: "Trip history, invoices, and payments",
  },
  {
    href: "/integrations",
    icon: "mdi-puzzle-outline",
    tone: "mint",
    title: "Widget & API",
    desc: "Domains, keys, and integration setup",
  },
  {
    href: "/api-docs",
    icon: "mdi-book-open-variant",
    tone: "orange",
    title: "API docs",
    desc: "Quotes, orders, webhooks, and payments",
  },
  {
    href: "/profile#finance",
    icon: "mdi-cash-multiple",
    tone: "mint",
    title: "Finance settings",
    desc: "Settlement mode and invoice email",
  },
];

const TICKET_TOPICS = [
  { id: "billing", label: "Billing & payments" },
  { id: "integrations", label: "Widget / Partner API" },
  { id: "trips", label: "Active trip issue" },
  { id: "account", label: "Account & profile" },
  { id: "other", label: "Something else" },
];

const FAQS = [
  {
    question: "How do I pay outstanding trip invoices?",
    answer:
      "Go to Billing, select unpaid trips, and pay via EFT using the banking details and reference shown. End-customer PayFast payments are handled separately and won't appear on your merchant billing page.",
    link: "/billing",
    linkLabel: "Open billing",
  },
  {
    question: "How does widget / API billing work?",
    answer:
      "Settlement mode (prepaid per trip vs postpaid invoice) and who pays each trip are set under Integrations → Billing. Your account profile Finance tab summarises the current setup.",
    link: "/integrations#partner-billing",
    linkLabel: "Integration billing",
  },
  {
    question: "How do I get Partner API access?",
    answer:
      "Open Integrations, complete the Partner API access request, and our team will provision keys once approved. Self-service key rotation is available after activation.",
    link: "/integrations#partner",
    linkLabel: "Partner API",
  },
  {
    question: "How do I track active deliveries?",
    answer:
      "All live and recent orders are on your dashboard. Click an order for driver location, status timeline, and documents.",
    link: "/dashboard",
    linkLabel: "Dashboard",
  },
  {
    question: "How do I book many orders at once?",
    answer:
      "Use the Partner API to quote and accept trips from your ERP, TMS, or scripts. Open Integrations to request access and view API docs.",
    link: "/integrations?type=partner",
    linkLabel: "Partner API",
  },
  {
    question: "How do I update my finance / invoice email?",
    answer:
      "Add or change your finance contact under Account → Business (finance email field). Integration billing can also specify a separate billing contact.",
    link: "/profile#business",
    linkLabel: "Business details",
  },
];

function waLink(number) {
  const digits = String(number || "").replace(/[^0-9]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

function telLink(number) {
  const digits = String(number || "").replace(/[^0-9+]/g, "");
  return digits ? `tel:${digits}` : null;
}

class Support extends Component {
  constructor(props) {
    super(props);
    this.state = {
      contacts: DEFAULT_CONTACTS,
      contactsLoading: true,
      account: null,
      topic: "other",
      subject: "",
      message: "",
      activeFaq: null,
      ticketSent: false,
      isSubmitting: false,
    };
  }

  componentDidMount() {
    document.title = "Support | QikTruck";
    this.fetchContacts();
    this.fetchAccount();
  }

  authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  });

  fetchContacts = () => {
    axios
      .get(`${baseURL}/customer-support-contacts`)
      .then((res) => {
        if (res.data?.success && res.data.data) {
          this.setState({
            contacts: { ...DEFAULT_CONTACTS, ...res.data.data },
            contactsLoading: false,
          });
        } else {
          this.setState({ contactsLoading: false });
        }
      })
      .catch(() => this.setState({ contactsLoading: false }));
  };

  fetchAccount = () => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) return;
    axios
      .get(`${baseURL}/merchant/account-profile`, { headers: this.authHeaders() })
      .then((res) => {
        if (res.data?.account) {
          this.setState({ account: res.data.account });
        }
      })
      .catch(() => {});
  };

  selectTicketTopic = (topic) => {
    const label = TICKET_TOPICS.find((t) => t.id === topic)?.label || "";
    this.setState({
      topic,
      subject: this.state.subject || (label ? `[${label}] ` : ""),
    });
  };

  handleInputChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  toggleFaq = (index) => {
    this.setState({ activeFaq: this.state.activeFaq === index ? null : index });
  };

  submitTicket = (e) => {
    e.preventDefault();
    const { subject, message, topic, contacts, account } = this.state;
    const topicLabel = TICKET_TOPICS.find((t) => t.id === topic)?.label || "Support";
    const email = contacts.email || DEFAULT_CONTACTS.email;

    const lines = [
      message.trim(),
      "",
      "---",
      `Topic: ${topicLabel}`,
      account?.customerid ? `Customer ID: ${account.customerid}` : null,
      account?.email ? `Account email: ${account.email}` : null,
      account?.firstname
        ? `Name: ${[account.firstname, account.lastname].filter(Boolean).join(" ")}`
        : null,
      "Sent from: QikTruck Business Portal",
    ].filter(Boolean);

    const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
      subject.trim() || `Business portal - ${topicLabel}`
    )}&body=${encodeURIComponent(lines.join("\n"))}`;

    this.setState({ isSubmitting: true });
    window.location.href = mailto;
    setTimeout(() => {
      this.setState({
        isSubmitting: false,
        ticketSent: true,
        subject: "",
        message: "",
        topic: "other",
      });
    }, 600);
  };

  renderChannels = () => {
    const { contacts, contactsLoading } = this.state;
    const whatsapp = contacts.whatsapp?.[0];
    const phone = contacts.call?.[0];
    const email = contacts.email || DEFAULT_CONTACTS.email;
    const hours = contacts.supportHours || DEFAULT_CONTACTS.supportHours;

    if (contactsLoading) {
      return (
        <div className="text-center py-4">
          <Spinner size="sm" color="primary" />
        </div>
      );
    }

    return (
      <>
        {whatsapp ? (
          <a
            href={waLink(whatsapp.number)}
            target="_blank"
            rel="noopener noreferrer"
            className="qt-channel-btn qt-channel-btn--whatsapp"
          >
            <span className="qt-channel-btn-icon qt-channel-btn-icon--whatsapp">
              <i className="mdi mdi-whatsapp" />
            </span>
            <span className="qt-channel-btn-text">
              <strong>{whatsapp.label || "WhatsApp"}</strong>
              <span>{whatsapp.number}</span>
            </span>
            <i className="mdi mdi-arrow-top-right ms-auto text-muted" />
          </a>
        ) : null}

        {phone ? (
          <a href={telLink(phone.number)} className="qt-channel-btn">
            <span className="qt-channel-btn-icon qt-channel-btn-icon--phone">
              <i className="mdi mdi-phone" />
            </span>
            <span className="qt-channel-btn-text">
              <strong>{phone.label || "Call us"}</strong>
              <span>{phone.number}</span>
            </span>
            <i className="mdi mdi-arrow-top-right ms-auto text-muted" />
          </a>
        ) : null}

        <a href={`mailto:${email}`} className="qt-channel-btn">
          <span className="qt-channel-btn-icon qt-channel-btn-icon--email">
            <i className="mdi mdi-email-outline" />
          </span>
          <span className="qt-channel-btn-text">
            <strong>Email</strong>
            <span>{email}</span>
          </span>
          <i className="mdi mdi-arrow-top-right ms-auto text-muted" />
        </a>

        <div className="qt-hours-chip">
          <i className="mdi mdi-clock-outline" />
          {hours}
        </div>

        {contacts.social && Object.keys(contacts.social).length > 0 ? (
          <div className="qt-social-row">
            {contacts.social.whatsapp ? (
              <a
                href={contacts.social.whatsapp}
                className="qt-social-link"
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
              >
                <i className="mdi mdi-whatsapp" />
              </a>
            ) : null}
            {contacts.social.facebook ? (
              <a href={contacts.social.facebook} className="qt-social-link" target="_blank" rel="noopener noreferrer" title="Facebook">
                <i className="mdi mdi-facebook" />
              </a>
            ) : null}
            {contacts.social.instagram ? (
              <a href={contacts.social.instagram} className="qt-social-link" target="_blank" rel="noopener noreferrer" title="Instagram">
                <i className="mdi mdi-instagram" />
              </a>
            ) : null}
            {contacts.social.linkedin ? (
              <a href={contacts.social.linkedin} className="qt-social-link" target="_blank" rel="noopener noreferrer" title="LinkedIn">
                <i className="mdi mdi-linkedin" />
              </a>
            ) : null}
          </div>
        ) : null}
      </>
    );
  };

  render() {
    const { subject, message, topic, activeFaq, ticketSent, isSubmitting, account } = this.state;

    return (
      <div className="qt-support qt-integrations">
        <Container>
          <div className="qt-page-head">
            <div className="qt-breadcrumb">
              <a href="/dashboard">Dashboard</a>
              <span className="mx-1">/</span>
              Support
            </div>
            <div className="qt-page-title-row">
              <div>
                <h1 className="qt-page-title">
                  <i className="mdi mdi-lifebuoy" />
                  Help &amp; support
                </h1>
                <p className="qt-page-lead mb-0">
                  Get help with trips, billing, integrations, and your business account. WhatsApp is
                  usually fastest during business hours.
                </p>
              </div>
              {account?.customerid ? (
                <div className="qt-site-chip">
                  <i className="mdi mdi-identifier" />
                  Customer ID <code>{account.customerid}</code>
                </div>
              ) : null}
            </div>
          </div>

          <div className="qt-help-grid">
            {HELP_TOPICS.map((item) => (
              <a key={item.href} href={item.href} className="qt-help-card">
                <div className={`qt-help-card-icon qt-help-card-icon--${item.tone}`}>
                  <i className={`mdi ${item.icon}`} />
                </div>
                <h6>{item.title}</h6>
                <p>{item.desc}</p>
              </a>
            ))}
          </div>

          <Row>
            <Col lg={7}>
              <div className="qt-card">
                <div className="qt-card-body">
                  <div className="qt-section-head">
                    <div className="qt-section-icon qt-section-icon--orange">
                      <i className="mdi mdi-email-edit-outline" />
                    </div>
                    <div>
                      <h4 className="qt-section-title">Send a support request</h4>
                      <p className="qt-section-desc mb-0">
                        Describe your issue and we&apos;ll open your email client with the details
                        pre-filled for our team.
                      </p>
                    </div>
                  </div>

                  {ticketSent ? (
                    <div className="qt-ticket-success">
                      <i className="mdi mdi-check-circle-outline d-block" />
                      <h5>Request ready to send</h5>
                      <p className="text-muted mb-3">
                        If your email app didn&apos;t open, email{" "}
                        <a href="mailto:support@qiktruck.co.za">support@qiktruck.co.za</a> directly.
                      </p>
                      <Button color="primary" outline size="sm" onClick={() => this.setState({ ticketSent: false })}>
                        Send another request
                      </Button>
                    </div>
                  ) : (
                    <Form onSubmit={this.submitTicket}>
                      <Label className="small text-muted fw-semibold">What is this about?</Label>
                      <div className="qt-topic-pills">
                        {TICKET_TOPICS.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className={"qt-topic-pill" + (topic === t.id ? " is-active" : "")}
                            onClick={() => this.selectTicketTopic(t.id)}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>

                      <FormGroup>
                        <Label for="subject">Subject</Label>
                        <Input
                          type="text"
                          name="subject"
                          id="subject"
                          placeholder="Brief summary of your issue"
                          value={subject}
                          onChange={this.handleInputChange}
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label for="message">Message</Label>
                        <Input
                          type="textarea"
                          name="message"
                          id="message"
                          rows={6}
                          placeholder="Include order IDs, domains, or error messages if relevant…"
                          value={message}
                          onChange={this.handleInputChange}
                          required
                        />
                      </FormGroup>

                      {account?.customerid ? (
                        <Alert color="light" className="py-2 small mb-3">
                          <i className="mdi mdi-information-outline me-1" />
                          Your customer ID and account email will be attached automatically.
                        </Alert>
                      ) : null}

                      <Button color="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Opening email…
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-send me-1" />
                            Send via email
                          </>
                        )}
                      </Button>
                    </Form>
                  )}
                </div>
              </div>

              <div className="qt-card">
                <div className="qt-card-body">
                  <div className="qt-section-head">
                    <div className="qt-section-icon qt-section-icon--teal">
                      <i className="mdi mdi-frequently-asked-questions" />
                    </div>
                    <div>
                      <h4 className="qt-section-title">Common questions</h4>
                      <p className="qt-section-desc mb-0">
                        Quick answers for business portal users.
                      </p>
                    </div>
                  </div>

                  <div className="qt-faq-list">
                    {FAQS.map((faq, index) => (
                      <div key={index} className="qt-faq-item">
                        <button
                          type="button"
                          className="qt-faq-question"
                          onClick={() => this.toggleFaq(index)}
                          aria-expanded={activeFaq === index}
                        >
                          {faq.question}
                          <i
                            className={
                              "mdi " + (activeFaq === index ? "mdi-chevron-up" : "mdi-chevron-down")
                            }
                          />
                        </button>
                        {activeFaq === index ? (
                          <div className="qt-faq-answer">
                            {faq.answer}
                            {faq.link ? (
                              <>
                                {" "}
                                <a href={faq.link}>{faq.linkLabel} →</a>
                              </>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={5}>
              <div className="qt-channel-card">
                <div className="qt-channel-card-head">
                  <h5>
                    <i className="mdi mdi-headset" />
                    Talk to us
                  </h5>
                </div>
                <div className="qt-channel-card-body">{this.renderChannels()}</div>
              </div>

              <div className="qt-card">
                <div className="qt-card-body">
                  <div className="qt-section-head mb-2">
                    <div className="qt-section-icon qt-section-icon--mint">
                      <i className="mdi mdi-lightning-bolt" />
                    </div>
                    <div>
                      <h4 className="qt-section-title">Urgent trip issue?</h4>
                      <p className="qt-section-desc mb-0">
                        For a delivery in progress, WhatsApp is fastest - include your order number.
                      </p>
                    </div>
                  </div>
                  <a
                    href={waLink(this.state.contacts.whatsapp?.[0]?.number)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success w-100"
                  >
                    <i className="mdi mdi-whatsapp me-1" />
                    Chat on WhatsApp
                  </a>
                </div>
              </div>

              <div className="qt-card">
                <div className="qt-card-body">
                  <h6 className="qt-card-heading mb-2">
                    <i className="mdi mdi-shield-alert me-1" />
                    Claims &amp; incidents
                  </h6>
                  <p className="small text-muted mb-2">
                    Damage or loss on a completed trip? Start from Integrations → Claims for the
                    correct form and process.
                  </p>
                  <a href="/integrations#claims" className="btn btn-outline-primary btn-sm">
                    Open claims info
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default Support;
